var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import debug from "debug";
import { localStore } from "@auvious/utils";
import { AUVIOUS_VALID_URL_REGEX, ErrorCode, KEY_CX_DFO, OriginModeEnum, WIDGET_COBROWSE_REQUEST_PREFIX, WIDGET_CONVERSATION_TYPE, WIDGET_MODE, WIDGET_REFERRER_ID, } from "./models/enums";
import { codeError } from "./utils";
import { Restorable } from "./models/Restorable";
const log = debug("auvious:cxone");
export class CXOneDFOChatService extends Restorable {
    constructor(config, handlers) {
        super();
        this.config = config;
        this.handlers = handlers;
        this.attachmentConstraints = {
            mimeType: "application/vnd.ms-powerpoint,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*,application/vnd.openxmlformats-officedocument.presentationml.presentation,video/*,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.ms-excel,audio/*",
            fileSize: 40 * 1024 * 1024,
            friendlyFileSize: "40 MiB",
        };
        this.isReconnecting = false;
        this.removeSDKListeners = () => { };
        // attachment id to its last known url
        this.attachments = {};
        if (config.customDomain) {
            this.customDomain = new RegExp(
            // escape any special characters in the stringToFind to prevent them from being interpreted as regex operators.
            config.customDomain.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i");
        }
        console.warn("CXOneDFOChatService", this);
        // TODO: also use src for older browsers
        import("@nice-devone/nice-cxone-chat-web-sdk").then((cx) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            this.cx = cx;
            (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.ready) === null || _b === void 0 ? void 0 : _b.call(_a);
        }));
    }
    reconnect(contact, conversationType) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            this.isReconnecting = true;
            try {
                const previousSession = this.getStorageSession();
                if (previousSession === null || previousSession === void 0 ? void 0 : previousSession.threadId) {
                    let { authenticationCode } = previousSession;
                    authenticationCode =
                        this.config.cxAuthorizationCode || authenticationCode;
                    if (authenticationCode) {
                        this.useAuthenticatedSessionCode(authenticationCode, false);
                    }
                    yield this.createChat(contact, conversationType);
                }
            }
            catch (ex) {
                log("Failed to reconnect to previous session", ex);
                this.setStorageSession(Object.assign(Object.assign({}, this.getStorageSession()), { threadId: undefined, customerId: undefined }));
                (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.disconnected) === null || _b === void 0 ? void 0 : _b.call(_a);
                this.sdk.getWebsocketClient().disconnect();
                this.destroy();
                throw codeError(ex, ErrorCode.INIT_CHAT_SERVICE_FAILED);
            }
            finally {
                this.isReconnecting = false;
            }
        });
    }
    openChannel() {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.session) {
                return;
            }
            let storageSession = this.getStorageSession() || {};
            const customerId = this.sessionContact.id ||
                storageSession.customerId ||
                this.cx.generateId();
            if (customerId !== storageSession.customerId) {
                this.setStorageSession((storageSession = Object.assign(Object.assign({}, storageSession), { customerId })));
            }
            (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.connecting) === null || _b === void 0 ? void 0 : _b.call(_a);
            const customerName = [
                this.sessionContact.firstName,
                this.sessionContact.lastName,
            ]
                .join(" ")
                .trim();
            this.sdk = new this.cx.ChatSdk({
                customerId,
                brandId: this.config.cxBrandId,
                channelId: this.config.cxChannelId,
                environment: this.config.cxEnvironment,
                customerName,
                customEnvironment: this.config.cxCustomEnvironment,
                onError(error) {
                    var _a, _b;
                    log(error);
                    (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.call(_a, codeError(error, ErrorCode.INIT_CHAT_SERVICE_FAILED));
                },
            });
            log("openChannel: id=", customerId, " name=", customerName, " sdk=", this.sdk);
            let consumerAuthorized;
            try {
                consumerAuthorized = storageSession.authenticationCode
                    ? yield this.sdk.authorize(storageSession.authenticationCode)
                    : yield this.sdk.authorize();
            }
            catch (ex) {
                throw codeError(ex, ErrorCode.AUTHENTICATION_ERROR);
            }
            (_c = this.sdk.getCustomer()) === null || _c === void 0 ? void 0 : _c.setName(customerName);
            // @ts-expect-error ts bs
            this.isLive = ((_d = consumerAuthorized.channel) === null || _d === void 0 ? void 0 : _d.settings.isLivechat) || false;
            log("openChannel: authorized=", consumerAuthorized, " isLive=", this.isLive);
            yield this.joinChat();
        });
    }
    handleMessage(messages, 
    /** TODO: to be removed if no other types will be used */
    eventTypes, isHistory = false) {
        messages === null || messages === void 0 ? void 0 : messages.forEach((message, i) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
            try {
                const id = message.idOnExternalPlatform;
                // const eventType = eventTypes[i];
                const isLocal = message.direction === "inbound";
                const timestamp = new Date(message.createdAt);
                const text = (_a = message.messageContent.payload) === null || _a === void 0 ? void 0 : _a.text;
                const status = "ack";
                const senderId = (((_b = message.authorUser) === null || _b === void 0 ? void 0 : _b.agentId) ||
                    ((_c = message.authorEndUserIdentity) === null || _c === void 0 ? void 0 : _c.idOnExternalPlatform)) + "";
                (_d = message.attachments) === null || _d === void 0 ? void 0 : _d.forEach((attachment) => {
                    var _a, _b;
                    // client can send one file at a time, we have used the predetermined message id for files
                    const attachId = isLocal && message.attachments.length === 1 ? id : attachment.id;
                    this.attachments[attachId] = attachment;
                    (_b = (_a = this.handlers).messageReceived) === null || _b === void 0 ? void 0 : _b.call(_a, {
                        id: attachId,
                        type: "file",
                        file: {
                            id: attachId,
                            name: attachment.friendlyName,
                            size: NaN,
                            previewUrl: attachment.previewUrl,
                        },
                        isLocal,
                        timestamp,
                        senderId,
                        status,
                    });
                });
                if (message.messageContent.type !== this.cx.MessageType.TEXT ||
                    !(text === null || text === void 0 ? void 0 : text.length)) {
                    // ignore invalid message
                    return;
                }
                if (AUVIOUS_VALID_URL_REGEX.test(text) ||
                    ((_e = this.customDomain) === null || _e === void 0 ? void 0 : _e.test(text))) {
                    // do not open a past call
                    if (!isHistory) {
                        log("CXOne: room created", text);
                        (_g = (_f = this.handlers).roomCreated) === null || _g === void 0 ? void 0 : _g.call(_f, text);
                    }
                }
                else if (text.indexOf(WIDGET_COBROWSE_REQUEST_PREFIX) > -1) {
                    // do not open a past co-browse request
                    if (!isHistory) {
                        log("CXOne: cobrowse requested", text);
                        // auvious:cobrowse-request:<request-type>:<ticket>
                        const request = text.split(":");
                        const requestType = request[2];
                        const ticket = request[3];
                        (_j = (_h = this.handlers).cobrowseRequested) === null || _j === void 0 ? void 0 : _j.call(_h, ticket, requestType);
                    }
                }
                else {
                    (_l = (_k = this.handlers).messageReceived) === null || _l === void 0 ? void 0 : _l.call(_k, {
                        id,
                        text,
                        type: "text",
                        isLocal,
                        timestamp,
                        status,
                        senderId,
                    });
                }
            }
            catch (ex) {
                log("Failed to handle message:", ex);
            }
        });
    }
    joinChat() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        return __awaiter(this, void 0, void 0, function* () {
            const session = this.getStorageSession();
            const threadId = (session === null || session === void 0 ? void 0 : session.threadId) || this.cx.generateId();
            this.session = this.sdk.getThread(threadId);
            this.session.setCustomField("auvious", JSON.stringify(this.getCustomerMetadata()));
            this.session.sendCustomFields();
            log("joinChat: setfields");
            const offAgentTyping = this.sdk.onChatEvent(this.cx.ChatEvent.AGENT_TYPING_STARTED, (event) => {
                var _a, _b;
                log("event: ", this.cx.ChatEvent.AGENT_TYPING_STARTED);
                (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.agentTypingStarted) === null || _b === void 0 ? void 0 : _b.call(_a);
            });
            const offAgentNotTyping = this.sdk.onChatEvent(this.cx.ChatEvent.AGENT_TYPING_ENDED, (event) => {
                var _a, _b;
                log("event: ", this.cx.ChatEvent.AGENT_TYPING_ENDED);
                (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.agentTypingStopped) === null || _b === void 0 ? void 0 : _b.call(_a);
            });
            const offContactCreated = this.sdk.onChatEvent(this.cx.ChatEvent.CONTACT_CREATED, (event) => {
                var _a, _b, _c, _d;
                log("event: ", this.cx.ChatEvent.CASE_CREATED, event.detail);
                (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.started({
                    id: event.detail.data.case.id,
                    memberId: (_b = this.sdk.getCustomer()) === null || _b === void 0 ? void 0 : _b.getId(),
                    uri: null,
                    jwt: null,
                });
                (_d = (_c = this.handlers) === null || _c === void 0 ? void 0 : _c.agentNotified) === null || _d === void 0 ? void 0 : _d.call(_c);
            });
            const offContactStatus = this.sdk.onChatEvent(this.cx.ChatEvent.CONTACT_STATUS_CHANGED, (event) => {
                log("event: ", this.cx.ChatEvent.CONTACT_STATUS_CHANGED, event);
                if (event.detail.data.case.status === this.cx.ContactStatus.CLOSED) {
                    this.endChat();
                }
            });
            const offAgentAssigned = this.sdk.onChatEvent(this.cx.ChatEvent.ASSIGNED_AGENT_CHANGED, (event) => {
                var _a, _b, _c, _d, _e, _f;
                log("event: ", this.cx.ChatEvent.CONTACT_STATUS_CHANGED, event.detail);
                const data = event.detail.data;
                // let closed be handled by contact status change
                if (data.case.status === this.cx.ContactStatus.CLOSED) {
                    return;
                }
                if (data.previousInboxAssignee) {
                    (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.agentLeft) === null || _b === void 0 ? void 0 : _b.call(_a);
                }
                if (data.inboxAssignee) {
                    (_d = (_c = this.handlers) === null || _c === void 0 ? void 0 : _c.agentJoined) === null || _d === void 0 ? void 0 : _d.call(_c, {
                        id: data.inboxAssignee.agentId,
                        joinDate: new Date().toISOString(),
                        displayName: data.inboxAssignee.firstName + " " + data.inboxAssignee.surname,
                        avatarImageUrl: data.inboxAssignee.publicImageUrl,
                        role: "AGENT",
                    });
                }
                else {
                    (_f = (_e = this.handlers) === null || _e === void 0 ? void 0 : _e.agentNotified) === null || _f === void 0 ? void 0 : _f.call(_e);
                }
            });
            // const offMessageCreated = this.session.onThreadEvent(
            //   this.cx.ChatEvent.MESSAGE_CREATED,
            //   (event: CustomEvent<MessageCreatedEvent>) => {
            //     log("event: ", this.cx.ChatEvent.MESSAGE_CREATED, event.detail);
            //     this.handleMessage(
            //       [event.detail.data.message],
            //       [this.cx.ChatEvent.MESSAGE_CREATED],
            //       false
            //     );
            //   }
            // );
            const offMessageAddedIntoCase = this.session.onThreadEvent(this.cx.ChatEvent.MESSAGE_ADDED_INTO_CASE, (event) => {
                log("event: ", this.cx.ChatEvent.MESSAGE_ADDED_INTO_CASE, event.detail);
                this.handleMessage([event.detail.data.message], [this.cx.ChatEvent.MESSAGE_ADDED_INTO_CASE], false);
            });
            this.removeSDKListeners = () => {
                offAgentTyping();
                offAgentNotTyping();
                offContactCreated();
                offAgentAssigned();
                // offMessageCreated();
                offMessageAddedIntoCase();
                offContactStatus();
            };
            if (this.isReconnecting && (session === null || session === void 0 ? void 0 : session.threadId)) {
                const { messages, contact } = yield this.session.recover();
                if (contact.status === this.cx.ContactStatus.CLOSED) {
                    throw new Error("Thread has been closed.");
                }
                (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.started) === null || _b === void 0 ? void 0 : _b.call(_a, {
                    // @ts-expect-error
                    id: contact.id,
                    memberId: (_c = this.sdk.getCustomer()) === null || _c === void 0 ? void 0 : _c.getId(),
                    uri: null,
                    jwt: null,
                });
                (_e = (_d = this.handlers) === null || _d === void 0 ? void 0 : _d.reconnected) === null || _e === void 0 ? void 0 : _e.call(_d);
                // connected is async and conflicts with agentJoined
                yield new Promise(requestAnimationFrame);
                if ("inboxAssigneeUser" in contact && contact.inboxAssigneeUser) {
                    (_g = (_f = this.handlers) === null || _f === void 0 ? void 0 : _f.agentJoined) === null || _g === void 0 ? void 0 : _g.call(_f, {
                        id: contact.inboxAssigneeUser.agentId + "",
                        joinDate: new Date().toISOString(),
                        displayName: contact.inboxAssigneeUser.firstName +
                            " " +
                            contact.inboxAssigneeUser.surname,
                        avatarImageUrl: contact.inboxAssigneeUser.publicImageUrl,
                        role: "AGENT",
                    });
                }
                else {
                    (_j = (_h = this.handlers) === null || _h === void 0 ? void 0 : _h.agentNotified) === null || _j === void 0 ? void 0 : _j.call(_h);
                }
                log("joinChat: recovered", messages, contact);
                // inital order is most recent first
                messages.reverse();
                this.handleMessage(messages, messages.map(() => this.cx.ChatEvent.MESSAGE_ADDED_INTO_CASE), true);
            }
            else {
                (_l = (_k = this.handlers) === null || _k === void 0 ? void 0 : _k.connected) === null || _l === void 0 ? void 0 : _l.call(_k);
                this.setStorageSession(Object.assign(Object.assign({}, session), { threadId }));
            }
            if (this.isLive)
                yield this.session.startChat();
        });
    }
    getCustomerMetadata() {
        return Object.assign(Object.assign({}, this.sessionContact), { [WIDGET_CONVERSATION_TYPE]: this.sessionConversationType, [WIDGET_REFERRER_ID]: window.origin, [WIDGET_MODE]: this.config.kioskMode
                ? OriginModeEnum.kiosk
                : OriginModeEnum.website });
    }
    getStorageSession() {
        const session = localStore.getItem(KEY_CX_DFO + this.config.cxChannelId);
        return session ? JSON.parse(session) : null;
    }
    setStorageSession(session) {
        localStore.setItem(KEY_CX_DFO + this.config.cxChannelId, JSON.stringify(session));
    }
    useAuthenticatedSessionCode(token, save = true) {
        this.authenticatedSessionToken = token;
        const session = this.getStorageSession();
        if (save) {
            this.setStorageSession(Object.assign(Object.assign({}, session), { authenticationCode: this.authenticatedSessionToken }));
        }
        log("useAuthenticatedSessionCode", session, token);
    }
    /** interface implementations ***/
    createChat(contact, conversationType, code) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            log("createChat:", contact, conversationType);
            if (code === null || code === void 0 ? void 0 : code.code) {
                this.useAuthenticatedSessionCode(code.code);
            }
            this.sessionContact = contact;
            this.sessionConversationType = conversationType;
            (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.starting) === null || _b === void 0 ? void 0 : _b.call(_a);
            yield this.openChannel();
        });
    }
    endChat() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.session) {
                return;
            }
            const session = this.getStorageSession();
            // clearing thread id makes sure that a new session is created next time
            if (session) {
                if (this.isLive)
                    yield this.session.endChat();
                this.session.archive();
                session.threadId = undefined;
                session.customerId = undefined;
                this.setStorageSession(session);
                log("endChat");
            }
            this.notifyChatEnded();
        });
    }
    notifyChatEnded() {
        var _a, _b, _c;
        if (this.session) {
            (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.customerLeft();
            (_b = this.handlers) === null || _b === void 0 ? void 0 : _b.agentLeft();
            (_c = this.handlers) === null || _c === void 0 ? void 0 : _c.ended();
            log("notifyChatEnded");
        }
        this.destroy();
    }
    destroy() {
        var _a;
        this.removeSDKListeners();
        (_a = this.sdk) === null || _a === void 0 ? void 0 : _a.getWebsocketClient().disconnect();
        this.session = null;
        this.sdk = null;
    }
    sendTyping() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.customerTypingEnabled) {
                return;
            }
            (_a = this.session) === null || _a === void 0 ? void 0 : _a.keystroke();
        });
    }
    sendMessage(message, bodyType, metadata) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            const messageId = this.cx.generateId();
            (_b = (_a = this.handlers).messageSent) === null || _b === void 0 ? void 0 : _b.call(_a, {
                id: messageId,
                text: message,
                type: "text",
                isLocal: true,
                timestamp: new Date(),
                status: "sent",
                senderId: this.sdk.getCustomer().getId(),
            });
            yield ((_c = this.session) === null || _c === void 0 ? void 0 : _c.sendTextMessage(message, { messageId }));
        });
    }
    acknowledgeMessages() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            yield ((_a = this.session) === null || _a === void 0 ? void 0 : _a.lastMessageSeen());
        });
    }
    sendFile(inputFileRef) {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            const file = inputFileRef.files[0];
            if (!file)
                return;
            const messageId = this.cx.generateId();
            const messageSent = {
                id: messageId,
                type: "file",
                file: {
                    id: messageId,
                    name: file.name,
                    size: file.size,
                },
                isLocal: true,
                timestamp: new Date(),
                status: "sent",
                senderId: this.sdk.getCustomer().getId(),
            };
            try {
                (_b = (_a = this.handlers).messageSent) === null || _b === void 0 ? void 0 : _b.call(_a, messageSent);
                yield ((_c = this.session) === null || _c === void 0 ? void 0 : _c.sendAttachments(inputFileRef.files, {
                    messageId,
                }));
            }
            catch (e) {
                log("Error uploading files", e);
                (_e = (_d = this.handlers).messageError) === null || _e === void 0 ? void 0 : _e.call(_d, Object.assign(Object.assign({}, messageSent), { status: "error" }));
                throw codeError(e, ErrorCode.FILE_TRANSFER_API_ERROR);
            }
        });
    }
    downloadFile(file) {
        return __awaiter(this, void 0, void 0, function* () {
            const attachment = this.attachments[file.id];
            if (!attachment) {
                throw codeError(new Error("file not found in attachments list"), ErrorCode.FILE_TRANSFER_API_ERROR);
            }
            try {
                open(attachment.url, "_blank");
            }
            catch (e) {
                log("Error downloading file", e);
                throw codeError(e, ErrorCode.FILE_TRANSFER_API_ERROR);
            }
        });
    }
    customerConnected() {
        return __awaiter(this, void 0, void 0, function* () {
            return !!this.sdk;
        });
    }
    get customerTypingEnabled() {
        return true;
    }
}
//# sourceMappingURL=cxone.chat.dfo.service.js.map